import { useAuth } from "@/features/auth/hooks/useAuth";
import { useFeatureFlags } from "@/features/config/useFeatureFlags";
import { UsageAlert } from "@/features/usage/components/UsageAlert";
import { useDailyMaps } from "@/features/usage/hooks/useLimit";
import { reportLimitError } from "@/features/usage/limitErrors";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { executeCli, executeCliStream, fetchSubgraph } from "../api/playground";
import { CitationGraphPanel } from "../components/CitationGraphPanel";
import { CitationsControls } from "../components/CitationsControls";
import { CommandPreview } from "../components/CommandPreview";
import { ConsoleControls } from "../components/ConsoleControls";
import { ConsolePanel, type ConsoleHandle } from "../components/ConsolePanel";
import { GrepControls } from "../components/GrepControls";
import { MapControls } from "../components/MapControls";
import { OperationTabs } from "../components/OperationTabs";
import { OutputPanel } from "../components/OutputPanel";
import { PaperDetailPanel } from "../components/PaperDetailPanel";
import { ProteinDetailPanel } from "../components/ProteinDetailPanel";
import { ResultSetPanel } from "../components/ResultSetPanel";
import { SearchControls } from "../components/SearchControls";
import { docAnchorId } from "../components/SearchResultsView";
import { PANEL, PANEL_HEAD, PANEL_RULE, RUN_BUTTON } from "../components/styles";
import type {
  CitationsFormState,
  DetailSource,
  GrepFormState,
  MapFormState,
  MapProgress,
  PaperDetailTarget,
  PipelineStep,
  PlaygroundOp,
  PlaygroundRun,
  SearchContext,
  SearchFormState,
  SubgraphResponse,
} from "../types";
import { buildCommand, isRunnable, quote } from "../utils/buildCommand";
import { parseGrepOutput, parseMapTasks } from "../utils/parseOutputs";

export const PlaygroundPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    isAtLimit: mapsAtLimit,
    isApproachingLimit: mapsApproaching,
    limit: mapsLimit,
    refetchUsage,
  } = useDailyMaps();
  const { flags } = useFeatureFlags();
  // The citation graph explorer ships behind its own flag so it can stay
  // hidden on prod while the playground itself is public. Citation counts in
  // search results are backend enrichment and don't go through this gate.
  const showCitations = flags.citegraphExplorer;

  const [op, setOp] = useState<PlaygroundOp>("search");
  const [search, setSearch] = useState<SearchFormState>({
    query: "",
    source: "pmc",
    trialsCountry: "",
    limit: 10,
    year: "",
    sort: "relevance",
    since: "",
    yearMin: "",
    yearMax: "",
    exact: false,
    titleOnly: false,
    author: false,
    hasFullText: false,
    boolMode: false,
    journal: "",
    hasSection: "",
    hasBlockType: "",
    excludeJournal: "",
    excludeYear: "",
  });
  const [grep, setGrep] = useState<GrepFormState>({
    pattern: "",
    scope: "corpus",
    paperId: "",
    source: "",
    ignoreCase: false,
    wholeWords: false,
    fixedStrings: false,
    countOnly: false,
    boolMode: false,
    maxMatches: "",
    context: "",
    section: "",
    blockType: "",
  });
  const [map, setMap] = useState<MapFormState>({
    question: "",
    limit: "",
    offset: "",
    outputSchema: "",
  });
  const [citations, setCitations] = useState<CitationsFormState>({
    paperId: "",
    perSide: 15,
  });

  /** Last successful search — map and `grep --from` run against this set. */
  const [searchContext, setSearchContext] = useState<SearchContext | null>(null);
  /** History of grep/map operations applied to the current result set. */
  const [steps, setSteps] = useState<PipelineStep[]>([]);
  /** Hits of the step whose output is currently displayed (dims minimap dots). */
  const [activeStepHits, setActiveStepHits] = useState<Record<
    string,
    number
  > | null>(null);
  /** Document nearest the top of the viewport — drives the highlighted dot. */
  const [activeDocId, setActiveDocId] = useState<string | null>(null);
  /** True while a removed filter step is being rolled back server-side. */
  const [restoring, setRestoring] = useState(false);

  const [running, setRunning] = useState(false);
  const [runningCommand, setRunningCommand] = useState<string | null>(null);
  const [mapProgress, setMapProgress] = useState<MapProgress | null>(null);
  const [run, setRun] = useState<PlaygroundRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<PaperDetailTarget | null>(null);
  /** UniProt accession open in the protein profile slide-over. */
  const [proteinDetail, setProteinDetail] = useState<string | null>(null);
  /** Citation neighborhood shown in the graph GUI (citations op). */
  const [subgraph, setSubgraph] = useState<SubgraphResponse | null>(null);
  const [graphError, setGraphError] = useState<string | null>(null);

  const consoleRef = useRef<ConsoleHandle>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  /** Sticky result-set bar — its height offsets the scroll-sync band. */
  const setPanelRef = useRef<HTMLDivElement>(null);

  // Never leave the UI parked on the citations tab when the explorer flag is
  // off — flags load async, so this also covers the flag resolving to false
  // after mount.
  useEffect(() => {
    if (!showCitations && op === "citations") setOp("search");
  }, [showCitations, op]);

  // Scroll-sync: as the results list scrolls, the document whose card last
  // crossed the "reading band" (just below the sticky result-set bar)
  // becomes active, and its dot lights up in the result-set minimap.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !searchContext) return;
    const papers = searchContext.papers;
    let raf = 0;
    const sync = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const panelH = setPanelRef.current?.offsetHeight ?? 0;
        const bandY = container.getBoundingClientRect().top + panelH + 100;
        let active: string | null = null;
        let anyVisible = false;
        for (const paper of papers) {
          const el = document.getElementById(docAnchorId(paper.document_id));
          if (!el) continue;
          anyVisible = true;
          if (el.getBoundingClientRect().top <= bandY) {
            active = paper.document_id;
          } else if (active) {
            break;
          }
        }
        if (anyVisible) {
          setActiveDocId(active ?? papers[0]?.document_id ?? null);
        }
      });
    };
    sync();
    container.addEventListener("scroll", sync, { passive: true });
    return () => {
      container.removeEventListener("scroll", sync);
      cancelAnimationFrame(raf);
    };
  }, [searchContext, run]);

  /** Minimap dot click → scroll the list to that document's card/row. */
  const scrollToDoc = (docId: string) => {
    setActiveDocId(docId);
    document
      .getElementById(docAnchorId(docId))
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  /** Gate console actions on auth; returns whether the caller may proceed. */
  const requireAuth = () => {
    if (user) return true;
    navigate("/login?next=/playground");
    return false;
  };

  // A result-set grep (or filter) narrows the set server-side and saves a new
  // results_id. Set-scoped ops (map, another `grep --from`) must chain from the
  // MOST RECENT narrowing step's set — not the original search — so `map` runs
  // over the grep's positive hits, matching the visible pipeline. Falls back to
  // the search set when no step has narrowed it yet.
  const lastNarrowing = [...steps].reverse().find((s) => s.resultsId);
  const workingResultsId =
    lastNarrowing?.resultsId ?? searchContext?.resultsId ?? null;
  const effectiveContext =
    searchContext && lastNarrowing?.resultsId
      ? {
        ...searchContext,
        resultsId: lastNarrowing.resultsId,
        // Narrow the visible set to the step's survivors so the map/grep
        // "operating on N papers" copy matches the id the command targets.
        papers: searchContext.papers.filter(
          (p) => lastNarrowing.hits[p.document_id] !== undefined,
        ),
      }
      : searchContext;

  const built = buildCommand(op, search, grep, map, effectiveContext, citations);
  const runnable = isRunnable(op, search, grep, map, effectiveContext, citations);

  /** The citations op renders a graph GUI, not the text output panel. */
  const runCitations = async (paperId: string, perSide: number) => {
    if (running || restoring) return;
    if (!user) {
      navigate("/login?next=/playground");
      return;
    }
    if (!paperId.trim()) return;
    setRunning(true);
    setGraphError(null);
    try {
      setSubgraph(await fetchSubgraph(paperId.trim(), perSide));
    } catch (e) {
      setSubgraph(null);
      setGraphError(e instanceof Error ? e.message : "Request failed");
    } finally {
      setRunning(false);
    }
  };

  /** Click-to-re-center from inside the graph. */
  const recenterGraph = (paperId: string) => {
    setCitations((prev) => ({ ...prev, paperId }));
    void runCitations(paperId, citations.perSide);
  };

  const handleRun = async () => {
    if (op === "citations") {
      void runCitations(citations.paperId, citations.perSide);
      return;
    }
    if (running || restoring) return;
    if (!user) {
      navigate("/login?next=/playground");
      return;
    }
    if (!runnable) return;
    if (op === "map" && mapsAtLimit) return;

    setRunning(true);
    setError(null);
    setRunningCommand(built.display);
    setMapProgress(null);
    const started = performance.now();
    try {
      const response =
        op === "map"
          ? await executeCliStream(built.command, built.raw, setMapProgress)
          : await executeCli(built.command, built.raw);
      if (op === "map") refetchUsage();

      const contextPapers = searchContext?.papers ?? [];
      const next: PlaygroundRun = {
        op,
        displayCommand: built.display,
        response,
        grep:
          op === "grep"
            ? parseGrepOutput(response.output, response.result_data)
            : null,
        grepVisual:
          op === "grep"
            ? {
              pattern: grep.pattern.trim(),
              // Corpus (slab) and result-set (SQL) grep both match
              // case-insensitively regardless of -i; only a single-paper grep
              // honors the toggle. Highlight to match what the backend did.
              ignoreCase: grep.scope === "paper" ? grep.ignoreCase : true,
              fixedStrings: grep.fixedStrings,
              wholeWords: grep.wholeWords,
            }
            : null,
        map:
          op === "map" && searchContext
            ? {
              question: map.question.trim(),
              resultsId: workingResultsId ?? searchContext.resultsId,
              tasks: parseMapTasks(response, contextPapers),
            }
            : null,
        searchSource:
          op === "search"
            ? search.source
            : op === "map"
              ? (searchContext?.source ?? null)
              : null,
        elapsedMs: Math.round(performance.now() - started),
      };
      setRun(next);

      // A corpus search (or corpus grep, below) becomes the result set that
      // set-scoped operations run against; it resets the pipeline history.
      if (op === "search") {
        const resultsId =
          response.result_data?.results_id ?? response.result_id ?? null;
        const papers = response.result_data?.papers ?? [];
        if (resultsId && papers.length > 0) {
          setSearchContext({
            resultsId,
            query: search.query.trim(),
            source: search.source,
            papers,
            baseCommand: {
              command: built.command,
              raw: built.raw,
              display: built.display,
            },
          });
          setSteps([]);
          setActiveDocId(null);
        }
        setActiveStepHits(null);
      } else if (
        op === "grep" &&
        grep.scope === "results" &&
        searchContext &&
        response.exit_code === 0
      ) {
        // Record which set members matched (grep prints only matching docs).
        const hits: Record<string, number> = {};
        for (const group of next.grep?.papers ?? []) {
          hits[group.documentId] = group.matchCount;
        }
        // The backend now saves grep hits as their own set; carry its id so a
        // following map / `grep --from` chains from the hits, not the full set.
        setSteps((prev) => [
          ...prev,
          {
            id: Date.now(),
            kind: "grep",
            label: built.display,
            hits,
            resultsId: next.grep?.resultsId ?? undefined,
          },
        ]);
        setActiveStepHits(hits);
      } else if (
        op === "grep" &&
        grep.scope === "corpus" &&
        next.grep?.resultsId &&
        next.grep.papers.length > 0
      ) {
        // A corpus grep also yields a saved result set — make it the live
        // base so search/grep/map can chain from it (titles unknown here).
        setSearchContext({
          resultsId: next.grep.resultsId,
          query: grep.pattern.trim(),
          source: "papers",
          papers: next.grep.papers.map((group) => ({
            document_id: group.documentId,
          })),
          baseCommand: {
            command: built.command,
            raw: built.raw,
            display: built.display,
          },
        });
        setSteps([]);
        setActiveDocId(null);
        setActiveStepHits(null);
      } else if (op === "map" && next.map) {
        const hits: Record<string, number> = {};
        for (const task of next.map.tasks) {
          if (task.status === "success") hits[task.documentId] = 1;
        }
        setSteps((prev) => [
          ...prev,
          {
            id: Date.now(),
            kind: "map",
            label: built.display.replace(/^paperclip\s+/, ""),
            hits,
          },
        ]);
        setActiveStepHits(hits);
      } else {
        // Corpus/single-paper grep isn't tied to the set — clear the overlay.
        setActiveStepHits(null);
      }
    } catch (e) {
      const limitMessage = reportLimitError(e, "playground", {
        refetchUsage,
        quotaAlertVisible: mapsAtLimit,
      });
      setRun(null);
      setError(limitMessage ?? (e instanceof Error ? e.message : "Request failed"));
    } finally {
      setRunning(false);
      setMapProgress(null);
    }
  };

  /** Drop the whole result set: back to stateless, corpus-scoped forms. */
  const resetSet = () => {
    setSearchContext(null);
    setSteps([]);
    setActiveStepHits(null);
    setActiveDocId(null);
    setGrep((prev) =>
      prev.scope === "results" ? { ...prev, scope: "corpus" } : prev,
    );
  };

  /**
   * Remove step `stepId` and everything after it, rolling the set back to
   * the state after the previous step.
   *
   * Grep/map steps never mutate the set, so dropping them is client-side
   * only. Filter rewrites the set in place server-side, so if any removed
   * step is a filter the base command is re-run (fresh results_id) and the
   * kept filter steps are re-applied against it.
   */
  const removeStep = async (stepId: number) => {
    if (!searchContext || restoring || running) return;
    const index = steps.findIndex((s) => s.id === stepId);
    if (index < 0) return;
    const kept = steps.slice(0, index);
    const removedHasFilter = steps
      .slice(index)
      .some((s) => s.kind === "filter");

    if (!removedHasFilter) {
      setSteps(kept);
      const lastFilter = [...kept].reverse().find((s) => s.kind === "filter");
      setActiveStepHits(lastFilter?.hits ?? null);
      return;
    }

    setRestoring(true);
    setError(null);
    try {
      const base = searchContext.baseCommand;
      const response = await executeCli(base.command, base.raw);

      let resultsId: string | null;
      let papers: typeof searchContext.papers;
      if (base.command === "grep") {
        const parsed = parseGrepOutput(response.output, response.result_data);
        resultsId = parsed?.resultsId ?? null;
        papers = (parsed?.papers ?? []).map((group) => ({
          document_id: group.documentId,
        }));
      } else {
        resultsId =
          response.result_data?.results_id ?? response.result_id ?? null;
        papers = response.result_data?.papers ?? [];
      }
      if (!resultsId || papers.length === 0) {
        throw new Error(
          "Couldn't roll back: re-running the original command returned no results.",
        );
      }

      // Re-apply the kept filter steps against the fresh set, refreshing
      // their hit maps from the actual survivors.
      const rebuilt: typeof steps = [];
      for (const step of kept) {
        if (step.kind === "filter" && step.filterQuery) {
          const filtered = await executeCli(
            "filter",
            `${quote(step.filterQuery)} --from ${resultsId}`,
          );
          const hits: Record<string, number> = {};
          for (const paper of filtered.result_data?.papers ?? []) {
            hits[paper.document_id] = 1;
          }
          rebuilt.push({
            ...step,
            hits,
            label: `filter ${quote(step.filterQuery)} --from ${resultsId}`,
          });
        } else {
          rebuilt.push(step);
        }
      }

      setSearchContext({ ...searchContext, resultsId, papers });
      setSteps(rebuilt);
      const lastFilter = [...rebuilt]
        .reverse()
        .find((s) => s.kind === "filter");
      setActiveStepHits(lastFilter?.hits ?? null);
      setActiveDocId(null);
      // Show the restored set in the output panel.
      setRun({
        op: base.command === "grep" ? "grep" : "search",
        displayCommand: base.display,
        response,
        grep:
          base.command === "grep"
            ? parseGrepOutput(response.output, response.result_data)
            : null,
        grepVisual: null,
        map: null,
        searchSource: searchContext.source,
        elapsedMs: response.elapsed_ms ?? 0,
      });
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to roll back the result set",
      );
    } finally {
      setRestoring(false);
    }
  };

  const openPaper = (
    docId: string,
    source: DetailSource,
    grepPattern?: string,
    grepLineNumbers?: number[],
    grepIgnoreCase?: boolean,
  ) =>
    setDetail({
      docId,
      source,
      grepPattern,
      grepLineNumbers,
      grepIgnoreCase,
    });

  const grepPaper = (docId: string) => {
    setOp("grep");
    setGrep((prev) => ({ ...prev, scope: "paper", paperId: docId }));
  };

  /** From a protein profile, pivot to a paper search on the gene name. */
  const searchPapersFor = (query: string) => {
    setProteinDetail(null);
    setOp("search");
    setSearch((prev) => ({ ...prev, query, source: "pmc" }));
  };

  return (
    // PageLayout's <main> is overflow-hidden, so the page provides its own
    // scroll container — without it, results below the fold are unreachable.
    <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-6xl px-8 py-8">
        <header className="mb-8">
          <h1 className="font-display text-4xl leading-none tracking-tight text-zinc-900">
            Playground
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-600">
            The Paperclip API, live. Search 11M+ full-text papers alongside FDA documents,
            clinical trials, patents, and protein databases; grep the whole corpus; then map
            a question across your results. Every run shows the exact{" "}
            <code className="font-mono text-sm text-zinc-500">paperclip</code> command, so
            you can take it straight to the{" "}
            <Link
              to="/docs"
              className="text-zinc-800 underline decoration-zinc-300 underline-offset-[3px] hover:text-zinc-900"
            >
              CLI
            </Link>
            .
          </p>
        </header>

        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <section aria-label="Playground controls" className={`${PANEL} space-y-6`}>
            <div className={PANEL_HEAD}>
              <OperationTabs
                value={op}
                onChange={setOp}
                hiddenOps={showCitations ? [] : ["citations"]}
              />
            </div>

            {op === "search" ? (
              <SearchControls
                form={search}
                onChange={setSearch}
                onSubmit={handleRun}
              />
            ) : op === "grep" ? (
              <GrepControls
                form={grep}
                context={effectiveContext}
                onChange={setGrep}
                onSubmit={handleRun}
              />
            ) : op === "map" ? (
              <>
                <UsageAlert
                  id="usage-alert-playground-maps"
                  name="dailyMaps"
                  isAtLimit={mapsAtLimit}
                  isApproachingLimit={mapsApproaching}
                  limit={mapsLimit}
                />
                <MapControls
                  form={map}
                  context={effectiveContext}
                  onChange={setMap}
                  onSubmit={handleRun}
                  onGoToSearch={() => setOp("search")}
                  disabled={mapsAtLimit}
                />
              </>
            ) : op === "citations" ? (
              <CitationsControls
                form={citations}
                context={searchContext}
                onChange={setCitations}
                onSubmit={handleRun}
              />
            ) : (
              <ConsoleControls
                signedIn={!!user}
                onRequireAuth={requireAuth}
                onAgentStart={() => consoleRef.current?.beginAgentTyping()}
                onAgentToken={(token) => consoleRef.current?.pushAgentText(token)}
                onAgentEnd={() => consoleRef.current?.endAgentTyping()}
                getHistory={() => consoleRef.current?.getHistory() ?? []}
              />
            )}

            {op !== "console" && (op !== "map" || searchContext) && (
              <div className={PANEL_RULE}>
                <CommandPreview
                  command={built.display}
                  cliCommand={built.command}
                  cliRaw={built.raw}
                />
              </div>
            )}

            {op !== "console" && (
              <div className={`${PANEL_RULE} flex items-baseline gap-4`}>
                {user ? (
                  <button
                    type="button"
                    onClick={handleRun}
                    disabled={!runnable || running || restoring || (op === "map" && mapsAtLimit)}
                    aria-describedby={
                      op === "map" && (mapsAtLimit || mapsApproaching)
                        ? "usage-alert-playground-maps"
                        : undefined
                    }
                    className={RUN_BUTTON}
                  >
                    {running ? "Running…" : "Run"}
                  </button>
                ) : (
                  <button type="button" onClick={handleRun} className={RUN_BUTTON}>
                    Sign in to run
                  </button>
                )}
                <p className="text-sm leading-5 text-zinc-400">Live endpoints; nothing is mocked.</p>
              </div>
            )}
          </section>

          {/* The console stays mounted across tab switches so its scrollback
            and command history survive; only its visibility toggles. */}
          <div className={op === "console" ? "contents" : "hidden"}>
            <ConsolePanel ref={consoleRef} onRequireAuth={requireAuth} />
          </div>
          {op === "citations" && (
            <CitationGraphPanel
              data={subgraph}
              loading={running}
              error={graphError}
              onRecenter={recenterGraph}
            />
          )}
          {op !== "console" && op !== "citations" && (
            <div className="flex min-w-0 flex-col gap-3">
              {/* The result-set bar sits above the full list at the same width
                and stays pinned while scrolling: the highlighted dot follows
                the card in view, and each grep/map on the set stacks a step
                row — the search's statefulness, made visible. */}
              {searchContext && (
                <div ref={setPanelRef} className="sticky top-0 z-10 bg-white pb-1">
                  <ResultSetPanel
                    context={searchContext}
                    steps={steps}
                    activeId={activeDocId}
                    highlight={activeStepHits}
                    restoring={restoring}
                    onDotClick={scrollToDoc}
                    onRemoveStep={removeStep}
                    onReset={resetSet}
                  />
                </div>
              )}
              <OutputPanel
                run={run}
                running={running}
                runningCommand={runningCommand}
                mapProgress={mapProgress}
                error={error}
                activeDocId={activeDocId}
                onOpenPaper={openPaper}
                onGrepPaper={grepPaper}
                onOpenProtein={setProteinDetail}
              />
            </div>
          )}
        </div>

        <PaperDetailPanel target={detail} onClose={() => setDetail(null)} />
        <ProteinDetailPanel
          accession={proteinDetail}
          onClose={() => setProteinDetail(null)}
          onSearchPapers={searchPapersFor}
        />
      </div>
    </div>
  );
};
